import React, { useEffect, useMemo, useRef } from 'react';

import { PlusIcon } from 'lucide-react';
import { FieldArrayWithId, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { FILTER_ACTIVE_CLASSES } from '@/components/ui/custom/ComplexFilter/filterStyles';
import { SearchableDropdownMenu } from '@/components/ui/custom/SearchableDropdownMenu/SearchableDropdownMenu';
import { cn } from '@/lib/utils';
import {
  closeLogsFilterPanel,
  openLogsFilterPanel,
  setLogsFiltersDropdownOpen,
} from '@/modules/analytics/redux/actions';
import { selectLogsFiltersPanel } from '@/modules/analytics/redux/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { FilterViewListItem, LogsAnalytics } from '@/shared/types/analytics';

import { SessionFilterDropdown } from './SessionFilterDropdown';
import { mapUiToDataModelType } from './sessionFilterUtils';
import { useFiltersData } from './useFiltersData';
import { FilterForm } from './useSessionFilters';

interface AllFiltersDropdownProps {
  savedViews?: FilterViewListItem[];
  onLoadView?: (viewId: string) => void;
  formMethods: UseFormReturn<FilterForm>;
  fields: FieldArrayWithId<FilterForm, 'filters', 'id'>[];
  addFilter: (filterType: string) => void;
  removeFilter: (index: number) => void;
  saveFilters: () => void;
  activeFilters: LogsAnalytics[];
}

/**
 * AllFiltersDropdown component that provides a searchable dropdown menu for filtering sessions.
 */
export const AllFiltersDropdown: React.FC<AllFiltersDropdownProps> = ({
  savedViews,
  onLoadView,
  formMethods,
  fields,
  addFilter,
  removeFilter,
  saveFilters,
  activeFilters,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { openFilter, openFilterIndex, dropdownOpen, openSource } =
    useSelector(selectLogsFiltersPanel);
  const shouldOpenDropdownAfterClose = useRef(false);

  const closeFilterDropdown = () => {
    dispatch(closeLogsFilterPanel());
  };

  // Watch for when filter closes and we should open dropdown
  useEffect(() => {
    if (openFilter === null && shouldOpenDropdownAfterClose.current) {
      shouldOpenDropdownAfterClose.current = false;
      dispatch(setLogsFiltersDropdownOpen(true));
    }
  }, [dispatch, openFilter]);

  const handleBackToMenu = (source?: 'back' | 'outside') => {
    closeFilterDropdown(); // Close the filter configuration
    // Only reopen the main filters menu when explicitly going back, not when clicking outside
    if (source === 'back') {
      dispatch(setLogsFiltersDropdownOpen(true));
    }
  };

  const handleFilterClick = (filterId: string) => {
    // Handle saved view selection
    if (filterId.startsWith('view:')) {
      const viewId = filterId.replace('view:', '');
      if (onLoadView) {
        onLoadView(viewId);
        dispatch(setLogsFiltersDropdownOpen(false));
      }
      return;
    }

    // Handle channel leaf items - open the channels filter configuration
    if (filterId.startsWith('channel:')) {
      const channel = filterId.split(':')[1];

      // Check if channels filter already exists
      const existingIndex = fields.findIndex((field) => field.type === 'channels');

      if (existingIndex >= 0) {
        // Pre-select the clicked channel if it's not already selected
        const currentChannels = formMethods.getValues(`filters.${existingIndex}.channels`) || [];
        if (!currentChannels.includes(channel)) {
          formMethods.setValue(`filters.${existingIndex}.channels`, [...currentChannels, channel]);
        }
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: 'channels', filterIndex: existingIndex, source: 'menu' })
        );
        return;
      } else {
        // Add new channels filter and pre-select the clicked channel
        const newIndex = fields.length;
        addFilter('channels');
        dispatch(
          openLogsFilterPanel({ filterKey: 'channels', filterIndex: newIndex, source: 'menu' })
        );

        // Pre-select the clicked channel in the new filter
        formMethods.setValue(`filters.${newIndex}.channels`, [channel]);
      }
      dispatch(setLogsFiltersDropdownOpen(false));
      return;
    }

    // Handle insight filters specially - they all go into one 'insightFilters' filter
    if (filterId.startsWith('insight-')) {
      const insightFiltersIndex = fields.findIndex((field) => field.type === 'insightFilters');

      if (insightFiltersIndex >= 0) {
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({
            filterKey: filterId,
            filterIndex: insightFiltersIndex,
            source: 'menu',
          })
        );
        return;
      } else {
        // Add new insightFilters filter
        const newIndex = fields.length;
        addFilter('insightFilters');
        dispatch(
          openLogsFilterPanel({ filterKey: filterId, filterIndex: newIndex, source: 'menu' })
        );
      }
      dispatch(setLogsFiltersDropdownOpen(false));
    } else {
      // Map UI filter types to data model types for comparison
      let dataModelType = filterId;
      if (filterId === 'language-models') {
        dataModelType = 'languageModelIds';
      }

      // Check if filter already exists
      const existingIndex = fields.findIndex((field) => field.type === dataModelType);

      if (existingIndex >= 0) {
        dispatch(setLogsFiltersDropdownOpen(false));
        dispatch(
          openLogsFilterPanel({ filterKey: filterId, filterIndex: existingIndex, source: 'menu' })
        );
        return;
      } else {
        // Add new filter
        const newIndex = fields.length;
        addFilter(filterId);
        dispatch(
          openLogsFilterPanel({ filterKey: filterId, filterIndex: newIndex, source: 'menu' })
        );
      }
      dispatch(setLogsFiltersDropdownOpen(false));
    }
  };

  const { menuGroups } = useFiltersData(brainId, handleFilterClick, savedViews, onLoadView);

  const activeFilterCount = activeFilters?.length || 0;

  // Recompute the active field index so the open editor survives array reshuffles after saves.
  const resolvedFilterIndex = useMemo(() => {
    if (!openFilter || openFilterIndex === null) {
      return null;
    }

    const dataModelType = openFilter.startsWith('insight-')
      ? 'insightFilters'
      : mapUiToDataModelType(openFilter);

    const latestIndex = fields.findIndex((field) => field.type === dataModelType);
    if (latestIndex !== -1) {
      return latestIndex;
    }

    return openFilterIndex;
  }, [fields, openFilter, openFilterIndex]);

  const effectiveFilterIndex =
    resolvedFilterIndex !== null && resolvedFilterIndex !== undefined
      ? resolvedFilterIndex
      : openFilterIndex;

  const handleDropdownOpenChange = (newOpen: boolean) => {
    // If trying to open the dropdown while a filter is open, close the filter first and then open dropdown
    if (newOpen && openFilter && openFilterIndex !== null) {
      shouldOpenDropdownAfterClose.current = true;
      closeFilterDropdown();
      return;
    }
    dispatch(setLogsFiltersDropdownOpen(newOpen));
  };

  return (
    <div className="relative">
      <SearchableDropdownMenu
        trigger={
          <Button
            variant="outline-neutral"
            size="sm"
            className={cn(activeFilterCount > 0 && FILTER_ACTIVE_CLASSES)}
          >
            <PlusIcon className="h-4 w-4" />
            {t('common.filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
        }
        groups={menuGroups}
        searchPlaceholder={t('brain_logs.searchFilters')}
        emptyMessage={t('brain_logs.no_filters_found')}
        maxHeight="max-h-[576px]"
        align="end"
        isOpen={dropdownOpen}
        onOpenChange={handleDropdownOpenChange}
      />
      {openFilter && effectiveFilterIndex !== null && openSource !== 'pill' && (
        <SessionFilterDropdown
          type={openFilter}
          key={openFilter}
          filterIndex={effectiveFilterIndex}
          onClose={handleBackToMenu}
          onApply={saveFilters}
          onClear={() => {
            if (effectiveFilterIndex !== null) {
              removeFilter(effectiveFilterIndex);
            }
            saveFilters();
            closeFilterDropdown();
          }}
        />
      )}
    </div>
  );
};
